import { Controller } from '@hotwired/stimulus'
import { delay } from '../utilities'
import { elementVisible, smoothScrollTo } from '../utilities.js'

export default class extends Controller {
  static outlets = ['tabs']
  static values = { authType: String, sampleApiKey: String }
  static targets = [
    'apiKeyButton',
    'apiKeyInput',
    'apiKeyValue',
    'checkbox',
    'downloadCSVButton',
    'downloadCSVOption',
    'exportOptions',
    'exportPreset',
    'importButton',
    'maxDeliverabilitySummary',
    'maxReachSummary',
    'oauthAuthorizeButton',
    'oauthDenyButton',
    'sendToIntegrationButton',
    'sendToIntegrationOption'
  ]

  connect() {
    this.initialized = false
    this.currentStep = null
    if (!elementVisible(this.element, 500)) return

    const { hash } = window.location
    let startingStepId = null

    if (hash) startingStepId = hash.replace('#', '').split('--')[1]

    this.initAnimation(null, startingStepId || 'connect_account')
  }

  activateStep(event, step = null) {
    if (!event) {
      this.tabsOutlet.activateTab(false, step, {
        updateURL: false,
        scroll: true
      })
    }

    const newStep = step || event.params.step
    if (this.currentStep === newStep) return

    this.currentStep = newStep

    // Cancel any ongoing animations
    if (
      this.animationCancelToken &&
      typeof this.animationCancelToken.cancel === 'function'
    ) {
      this.animationCancelToken.cancel()
    }

    // Create a fresh cancel token for the current animation
    this.animationCancelToken = this.#createCancelToken()

    if (newStep === 'connect_account') {
      this.#resetSelectDataAnimationState()
      this.#resetExportAnimationState()
      this.#initConnectAccountAnimation(this.animationCancelToken)
    } else if (newStep === 'select_data') {
      this.#resetExportAnimationState()
      this.#resetConnectAccountAnimationState()
      this.#initSelectDataAnimation(this.animationCancelToken)
    } else {
      this.#resetSelectDataAnimationState()
      this.#resetConnectAccountAnimationState()
      this.#initExportAnimation(this.animationCancelToken)
    }

    this.initialized = true
  }

  initAnimation(_event, step = null) {
    if (!elementVisible(this.element, 200) || this.initialized) return

    if (step) {
      this.activateStep(null, step)
    } else {
      const { hash } = window.location
      this.activateStep(null, hash?.replace('#', '') || 'connect_account')
    }

    this.initialized = true
  }

  #createCancelToken() {
    return {
      cancelled: false,
      cancel() {
        this.cancelled = true
      }
    }
  }

  #initConnectAccountAnimation(token) {
    if (this.authTypeValue === 'api-key') {
      this.#initAPIKeyAnimation(token)
    } else if (this.authTypeValue === 'oauth') {
      this.#initOAuthAnimation(token)
    }
  }

  async #initAPIKeyAnimation(token) {
    await delay(1500)
    if (token.cancelled) return

    this.apiKeyInputTarget.classList.add('focus')
    this.apiKeyInputTarget.dataset.focused = true

    await delay(1500)
    if (token.cancelled) return

    this.apiKeyValueTarget.textContent = this.sampleApiKeyValue

    await delay(1000)
    if (token.cancelled) return

    this.apiKeyInputTarget.classList.remove('focus')
    this.apiKeyInputTarget.dataset.focused = false
    this.apiKeyButtonTarget.classList.add('animate-button-click')

    await delay(120)
    if (token.cancelled) return

    this.apiKeyButtonTarget.dataset.loading = true

    await delay(1500)
    if (token.cancelled) return

    this.apiKeyButtonTarget.dataset.loading = false
    this.apiKeyButtonTarget.dataset.complete = true

    await delay(2000)
    if (token.cancelled) return

    this.activateStep(null, 'select_data')
  }

  async #initOAuthAnimation(token) {
    await delay(1500)
    this.oauthAuthorizeButtonTarget.classList.add('animate-button-click')
    this.oauthDenyButtonTarget.disabled = true

    await delay(120)
    if (token.cancelled) return

    this.oauthAuthorizeButtonTarget.dataset.loading = true

    await delay(1500)
    if (token.cancelled) return

    this.oauthAuthorizeButtonTarget.dataset.loading = false
    this.oauthAuthorizeButtonTarget.dataset.complete = true

    await delay(2000)
    if (token.cancelled) return

    this.activateStep(null, 'select_data')
  }

  async #initSelectDataAnimation(token) {
    async function checkLists() {
      let index = 0

      for (const checkbox of this.checkboxTargets) {
        checkbox.classList.add('animate-button-click')

        await delay(120)
        if (token.cancelled) return

        checkbox.checked = true

        if (index === this.checkboxTargets.length - 1) return

        await delay(600)
        if (token.cancelled) return

        index++
      }
    }

    await delay(1500)
    if (token.cancelled) return

    await checkLists.call(this)

    await delay(1200)
    if (token.cancelled) return

    this.importButtonTarget.classList.add('animate-button-click')

    await delay(120)
    if (token.cancelled) return

    this.importButtonTarget.dataset.loading = true

    await delay(1500)
    if (token.cancelled) return

    this.importButtonTarget.dataset.loading = false
    this.importButtonTarget.dataset.complete = true

    await delay(2000)
    if (token.cancelled) return

    this.activateStep(null, 'export_results')
  }

  async #initExportAnimation(token) {
    await delay(1500)
    if (token.cancelled) return

    // simulate click on "Send to [Integration]" option
    this.sendToIntegrationOptionTarget.classList.add('animate-button-click')

    await delay(120)
    if (token.cancelled) return

    // select "Send to [Integration]", deselect "Download as CSV"
    this.downloadCSVOptionTarget.dataset.selected = false
    this.sendToIntegrationOptionTarget.dataset.selected = true

    // hide "Download as CSV" button, show "Send to Integration" button
    this.downloadCSVButtonTarget.hidden = true
    this.sendToIntegrationButtonTarget.hidden = false

    await delay(1200)
    if (token.cancelled) return

    // scroll export options into view (only under 528px screen width)
    if (window.innerWidth <= 527) {
      const scrollDistance =
        this.exportOptionsTarget.scrollHeight -
        this.exportOptionsTarget.getBoundingClientRect().height

      smoothScrollTo(scrollDistance, this.exportOptionsTarget, 'y', 500)

      await delay(1200)
      if (token.cancelled) return
    }

    this.exportPresetTargets[1].classList.add('animate-button-click')

    await delay(120)
    if (token.cancelled) return

    this.exportPresetTargets[0].dataset.selected = false
    this.exportPresetTargets[1].dataset.selected = true
    this.maxDeliverabilitySummaryTarget.classList.add('opacity-0')
    this.maxReachSummaryTarget.classList.remove('opacity-0')

    await delay(1500)
    if (token.cancelled) return

    this.sendToIntegrationButtonTarget.classList.add('animate-button-click')

    await delay(120)
    if (token.cancelled) return

    this.sendToIntegrationButtonTarget.dataset.loading = true

    await delay(1500)
    if (token.cancelled) return

    this.sendToIntegrationButtonTarget.dataset.loading = false
    this.sendToIntegrationButtonTarget.dataset.complete = true

    await delay(2000)
    if (token.cancelled) return

    this.activateStep(null, 'connect_account')
  }

  async #resetAPIKeyAnimationState() {
    await delay(300)

    // reset "input"
    this.apiKeyValueTarget.textContent = ''
    this.apiKeyInputTarget.classList.remove('focus')
    this.apiKeyInputTarget.dataset.focused = false

    // reset button
    this.apiKeyButtonTarget.dataset.loading = false
    this.apiKeyButtonTarget.dataset.complete = false
    this.apiKeyButtonTarget.classList.remove('animate-button-click')
  }

  async #resetOAuthAnimationState() {
    await delay(300)

    // reset authorize button
    this.oauthAuthorizeButtonTarget.dataset.loading = false
    this.oauthAuthorizeButtonTarget.dataset.complete = false
    this.oauthAuthorizeButtonTarget.classList.remove('animate-button-click')

    // reset deny button
    this.oauthDenyButtonTarget.disabled = false
  }

  async #resetConnectAccountAnimationState() {
    await delay(300)

    if (this.authTypeValue === 'api-key') {
      this.#resetAPIKeyAnimationState()
    } else {
      this.#resetOAuthAnimationState()
    }
  }

  async #resetSelectDataAnimationState() {
    await delay(300)

    // reset checkboxes
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
      checkbox.classList.remove('animate-button-click')
    })

    // reset import button
    this.importButtonTarget.dataset.loading = false
    this.importButtonTarget.dataset.complete = false
    this.importButtonTarget.classList.remove('animate-button-click')
  }

  async #resetExportAnimationState() {
    await delay(300)

    // reset export options scroll position
    this.exportOptionsTarget.scrollTop = 0

    // reset export destinations
    this.downloadCSVOptionTarget.dataset.selected = true
    this.sendToIntegrationOptionTarget.dataset.selected = false
    this.sendToIntegrationOptionTarget.classList.remove('animate-button-click')

    // reset export presets
    this.exportPresetTargets[0].dataset.selected = true
    this.exportPresetTargets[1].dataset.selected = false
    this.exportPresetTargets[1].classList.remove('animate-button-click')

    // reset export summaries
    this.maxDeliverabilitySummaryTarget.classList.remove('opacity-0')
    this.maxReachSummaryTarget.classList.add('opacity-0')

    // reset export buttons
    this.downloadCSVButtonTarget.hidden = false
    this.sendToIntegrationButtonTarget.hidden = true
    this.sendToIntegrationButtonTarget.dataset.loading = false
    this.sendToIntegrationButtonTarget.dataset.complete = false
    this.sendToIntegrationButtonTarget.classList.remove('animate-button-click')
  }
}
